/**
 * スリーブとデッキイラストの画像（/decks/images。Issue #958）。種類ごとのタブで一覧・アップロード・削除。
 * 消せるのは使っているデッキが無い画像だけ（サーバーが断る）。消した画像は S3 の deletedDeckImages/ へ退避される。
 * デッキイラストは「カード画像から作る」: カードと型番（画像）を選ぶと、絵の枠の中を比率で機械的に切り出す（`CropCardIllust`。Issue #959）。
 * そのまま保存するか、ダウンロードして手元で背景を消してからアップロードする。
 * デッキイラストは「中心」で中心にしたい 1 点を決める（Godot の演出がその点を中心に 1.6:1 で切り出す。SetDeckImageFocus。Issue #960）。
 * アップロードする画像は今の画像と同じ比率・大きさにそろえる（デッキイラスト 500 × 500・スリーブ 743 × 1080。utils/imageFit.js）。
 */
import { useCallback, useEffect, useState } from 'react';
import { usePageTitle } from '../appInfo';
import { api } from '../api/api';
import DeckNav from '../components/deck/DeckNav';
import { DECK_IMAGE_SIZES, fitImageFile, focusRegion } from '../utils/imageFit';

const SECTIONS = [
  { kind: 'illust', label: 'デッキイラスト', upload: 'UploadDeckIllust', w: 80, h: 80 },
  { kind: 'sleeve', label: 'スリーブ', upload: 'UploadSleeveImage', w: 59, h: 80 },
];

/** カード画像から絵の部分を切り出してデッキイラストにする */
function FromCardModal({ onClose, onSaved }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [card, setCard] = useState(null);       // 選んだカード
  const [images, setImages] = useState([]);     // そのカードの画像（型番・レアリティ）
  const [crop, setCrop] = useState(null);       // { card_image_id, name, data_url }
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!query.trim()) { setResults([]); return undefined; }
    const t = setTimeout(() => api.project('SearchCards', { query: query.trim(), has_image: true, limit: 30 })
      .then((r) => setResults(Array.isArray(r) ? r : [])).catch(() => setResults([])), 300);
    return () => clearTimeout(t);
  }, [query]);

  const pickCard = async (c) => {
    setCard(c); setCrop(null);
    try { setImages(await api.project('GetCardImage', { card_id: String(c.card_id) }) ?? []); } catch { setImages([]); }
  };
  const pickImage = async (img) => {
    setBusy(true);
    try { setCrop({ card_image_id: img.id, ...(await api.project('CropCardIllust', { card_image_id: img.id })) }); }
    catch (err) { alert('切り出せませんでした: ' + err.message); }
    finally { setBusy(false); }
  };
  const save = async () => {
    setBusy(true);
    try {
      await api.project('CropCardIllust', { card_image_id: crop.card_image_id, save: true });
      onSaved();
    } catch (err) { alert('保存できませんでした: ' + err.message); setBusy(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 640, width: '95vw' }} onClick={(e) => e.stopPropagation()} data-testid="from-card">
        <h2 className="modal-title">カード画像から作る</h2>
        <input className="form-input" placeholder="カード名で探す（画像のあるカード）" value={query} autoFocus
          onChange={(e) => setQuery(e.target.value)} data-testid="from-card-query" style={{ width: '100%', marginBottom: 8 }} />
        {!card && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: '40vh', overflowY: 'auto' }}>
            {results.map((c) => (
              <button key={c.card_id} className="btn btn-secondary btn-sm" style={{ textAlign: 'left', justifyContent: 'flex-start' }}
                onClick={() => pickCard(c)} data-testid={`from-card-${c.card_id}`}>{c.card_name}</button>
            ))}
          </div>
        )}
        {card && (
          <>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
              <strong>{card.card_name}</strong>
              <button className="btn btn-sm btn-secondary" onClick={() => { setCard(null); setCrop(null); }}>カードを選び直す</button>
            </div>
            <div className="card-meta" style={{ marginBottom: 4 }}>型番（画像）を選ぶと、絵の部分を切り出します</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
              {images.map((img) => (
                <button key={img.id} className={`btn btn-sm ${crop?.card_image_id === img.id ? 'btn-primary' : 'btn-secondary'}`} disabled={busy}
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, padding: 4 }}
                  onClick={() => pickImage(img)} data-testid={`from-card-image-${img.id}`}>
                  <img src={img.thumbnail_url || img.download_url} alt="" style={{ width: 54, borderRadius: 2 }} />
                  <span style={{ fontSize: '0.7rem' }}>{img.set_code || img.language_code}{img.rarity ? ` ${img.rarity}` : ''}</span>
                </button>
              ))}
            </div>
          </>
        )}
        {crop && (
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }} data-testid="from-card-preview">
            <img src={crop.data_url} alt={crop.name} style={{ width: 200, height: 200, objectFit: 'contain', background: '#1a1a2e', borderRadius: 4 }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div className="card-meta">{crop.name}</div>
              <a className="btn btn-secondary btn-sm" href={crop.data_url} download={crop.name} data-testid="from-card-download">ダウンロード（手元で背景を消す）</a>
              <button className="btn btn-primary btn-sm" disabled={busy} onClick={save} data-testid="from-card-save">デッキイラストとして保存</button>
            </div>
          </div>
        )}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>閉じる</button>
        </div>
      </div>
    </div>
  );
}

/** 中心にしたい点を押して決める（見本に Godot が切り出す 1.6:1 の範囲を重ねる） */
function FocusModal({ row, onClose, onSaved }) {
  const [point, setPoint] = useState(row.focus ?? [0.5, 0.5]);
  const [size, setSize] = useState(null);   // 絵の大きさ（範囲の計算に使う）
  const [saving, setSaving] = useState(false);
  const pick = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setPoint([Math.min(Math.max((e.clientX - r.left) / r.width, 0), 1), Math.min(Math.max((e.clientY - r.top) / r.height, 0), 1)]);
  };
  const save = async (clear = false) => {
    setSaving(true);
    try {
      await api.project('SetDeckImageFocus', clear ? { key: row.key } : { key: row.key, fx: point[0], fy: point[1] });
      onSaved();
    } catch (err) { alert('保存できませんでした: ' + err.message); setSaving(false); }
  };
  const region = size ? focusRegion(size.w, size.h, point) : null;
  const pct = (v) => `${(v * 100).toFixed(2)}%`;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 560, width: '95vw' }} onClick={(e) => e.stopPropagation()} data-testid="focus-modal">
        <h2 className="modal-title">中心にしたい点 — {row.name}</h2>
        <p className="card-meta" style={{ marginBottom: 8 }}>顔など目立たせたい所を押してください。枠は Godot の演出が切り出す範囲（横 1.6 : 縦 1）です。</p>
        <div style={{ position: 'relative', width: '100%', maxWidth: 480, margin: '0 auto', cursor: 'crosshair', userSelect: 'none' }}
          onClick={pick} data-testid="focus-image">
          <img src={row.url} alt={row.name} draggable={false} style={{ width: '100%', display: 'block', borderRadius: 4 }}
            onLoad={(e) => setSize({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })} />
          {region && (
            <div style={{ position: 'absolute', left: pct(region.x), top: pct(region.y), width: pct(region.w), height: pct(region.h),
              border: '2px solid #e94560', boxShadow: '0 0 0 9999px rgba(0,0,0,0.45)', pointerEvents: 'none' }} />
          )}
          <div style={{ position: 'absolute', left: pct(point[0]), top: pct(point[1]), width: 14, height: 14, marginLeft: -7, marginTop: -7,
            borderRadius: '50%', border: '2px solid #fff', background: '#e94560', pointerEvents: 'none' }} />
        </div>
        <div className="modal-footer">
          {row.focus && <button className="btn btn-secondary" disabled={saving} onClick={() => save(true)}>中心を消す</button>}
          <button className="btn btn-secondary" onClick={onClose}>キャンセル</button>
          <button className="btn btn-primary" disabled={saving} onClick={() => save()} data-testid="focus-save">保存</button>
        </div>
      </div>
    </div>
  );
}

function ImageSection({ section }) {
  const [rows, setRows] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [fromCard, setFromCard] = useState(false);
  const [focusRow, setFocusRow] = useState(null);
  const load = useCallback(() => api.project('GetDeckImages', { kind: section.kind })
    .then((r) => setRows(Array.isArray(r) ? r : [])).catch(() => setRows([])), [section.kind]);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const files = [...(e.target.files ?? [])];
    e.target.value = '';
    if (!files.length) return;
    setUploading(true);
    try {
      for (const original of files) {
        const file = await fitImageFile(original, ...DECK_IMAGE_SIZES[section.kind]);   // 今の画像と同じ比率・大きさに
        const { upload_link: link } = await api.project(section.upload, { filename: file.name });
        await fetch(link, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
      }
      await load();
    } catch (err) { alert('アップロードできませんでした: ' + err.message); }
    finally { setUploading(false); }
  };

  const remove = async (row) => {
    if (!window.confirm(`「${row.name}」を消しますか？`)) return;
    const res = await api.projectResult('DeleteDeckImage', { kind: section.kind, key: row.key });
    if (!res.ok) { alert(res.body?.error ?? '消せませんでした'); return; }
    load();
  };

  return (
    <section style={{ marginBottom: 28 }} data-testid={`deck-images-${section.kind}`}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10 }}>
        <h2 style={{ fontSize: '1rem', margin: 0 }}>{section.label}{rows ? `（${rows.length}）` : ''}</h2>
        <label className={`btn btn-primary btn-sm${uploading ? ' disabled' : ''}`} style={{ cursor: uploading ? 'not-allowed' : 'pointer' }}>
          {uploading ? 'アップロード中...' : '画像をアップロード'}
          <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={upload} disabled={uploading}
            data-testid={`upload-${section.kind}`} />
        </label>
        {section.kind === 'illust' && (
          <button className="btn btn-secondary btn-sm" onClick={() => setFromCard(true)} data-testid="open-from-card">カード画像から作る</button>
        )}
      </div>
      {focusRow && <FocusModal row={focusRow} onClose={() => setFocusRow(null)} onSaved={() => { setFocusRow(null); load(); }} />}
      {fromCard && <FromCardModal onClose={() => setFromCard(false)} onSaved={() => { setFromCard(false); load(); }} />}
      {!rows && <p className="loading">読み込み中...</p>}
      {rows?.length === 0 && <p className="loading">画像がありません</p>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {(rows ?? []).map((r) => (
          <div key={r.key} className="card" style={{ padding: 8, display: 'flex', gap: 12, alignItems: 'center' }} data-testid={`row-${r.name}`}>
            <a href={r.url} target="_blank" rel="noreferrer" style={{ flexShrink: 0 }}>
              <img src={r.thumbnail_url || r.url} alt={r.name}
                style={{ width: section.w * 0.75, height: section.h * 0.75, objectFit: 'cover', borderRadius: 4, background: '#1a1a2e', display: 'block' }} />
            </a>
            <div style={{ flex: 1, minWidth: 0, fontSize: '0.85rem', wordBreak: 'break-all' }}>{r.name}</div>
            {section.kind === 'illust' && (
              <button className="btn btn-secondary btn-sm" onClick={() => setFocusRow(r)} data-testid={`focus-${r.name}`}
                title="Godot の演出で真ん中に来る点">{r.focus ? '中心 ✓' : '中心を決める'}</button>
            )}
            <span title={r.used_by?.length ? `使っているデッキがあるので消せません: ${r.used_by.map((d) => d.name).join('、')}` : '消す'}>
              <button className="btn btn-danger btn-sm" disabled={!!r.used_by?.length} onClick={() => remove(r)}
                data-testid={`delete-${r.name}`}>削除</button>
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function DeckImages() {
  usePageTitle('デッキの画像');
  const [kind, setKind] = useState(SECTIONS[0].kind);
  const section = SECTIONS.find((s) => s.kind === kind);
  return (
    <>
      <DeckNav />
      <div className="page">
        <div className="page-header"><h1 className="page-title">デッキの画像</h1></div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 14 }} data-testid="deck-images-tabs">
          {SECTIONS.map((s) => (
            <button key={s.kind} className={`btn btn-sm ${kind === s.kind ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setKind(s.kind)}
              data-testid={`deck-images-tab-${s.kind}`}>{s.label}</button>
          ))}
        </div>
        <ImageSection key={section.kind} section={section} />
      </div>
    </>
  );
}
